import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

export type Theme = 'dark' | 'light';
export type Density = 'spacious' | 'balanced' | 'dense';

interface ThemeState {
  theme: Theme;
  density: Density;
}

interface ThemeContextValue extends ThemeState {
  setTheme: (t: Theme) => void;
  setDensity: (d: Density) => void;
  toggleTheme: () => void;
}

const STORAGE_KEY = 'kavrenix.tweaks';
const DEFAULTS: ThemeState = { theme: 'dark', density: 'spacious' };

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function AppThemeProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ThemeState>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) return { ...DEFAULTS, ...JSON.parse(raw) };
    } catch { /* ignore */ }
    return DEFAULTS;
  });

  // Reflect onto <html> so CSS tokens respond, and persist.
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', state.theme);
    root.setAttribute('data-density', state.density);
    root.style.colorScheme = state.theme === 'light' ? 'light' : 'dark';
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // ignore storage errors
    }
  }, [state]);

  const setTheme = useCallback(
    (theme: Theme) => setState((s) => ({ ...s, theme })),
    [],
  );

  const setDensity = useCallback(
    (density: Density) => setState((s) => ({ ...s, density })),
    [],
  );

  const toggleTheme = useCallback(
    () => setState((s) => ({ ...s, theme: s.theme === 'dark' ? 'light' : 'dark' })),
    [],
  );

  return (
    <ThemeContext.Provider value={{ ...state, setTheme, setDensity, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useAppTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useAppTheme must be used inside <AppThemeProvider>');
  return ctx;
}
